import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuthFetch } from '../hooks/useAuthFetch';
import ModaleModificaAllenamento from './ModaleModificaAllenamento';
import '../stili/AggiornaAllenamenti.css';

const API_URL = process.env.REACT_APP_API_URL;
const TIPI_ALLENAMENTO = ['TECNICO', 'TATTICO', 'ATLETICO'];

export default function AggiornaAllenamenti() {
  const authFetch = useAuthFetch();
  const navigate = useNavigate();

  const [categorie, setCategorie] = useState([]);
  const [staffList, setStaffList] = useState([]);

  const [idCategoria, setIdCategoria] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const [allenamenti, setAllenamenti] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingCategorie, setLoadingCategorie] = useState(true);
  const [loadingStaff, setLoadingStaff] = useState(true);

  const [modalOpen, setModalOpen] = useState(false);
  const [selected, setSelected] = useState(null);

  const parseErrorMessage = async (res, fallbackMessage) => {
    try {
      const contentType = res.headers.get('content-type') || '';

      if (contentType.includes('application/json')) {
        const data = await res.json();
        return data?.message || data?.error || data?.details || fallbackMessage;
      }

      const text = await res.text();
      return text || fallbackMessage;
    } catch {
      return fallbackMessage;
    }
  };

  const normalizeCategorie = (data) => {
    if (!Array.isArray(data)) return [];

    return data
      .map((c) => ({
        id: c.id ?? c.idCategoria ?? '',
        nome: c.nome ?? c.nomeCategoria ?? ''
      }))
      .filter((c) => c.id !== '' && c.nome);
  };

  const normalizeStaff = (data) => {
    if (!Array.isArray(data)) return [];

    return data
      .map((s) => ({
        id: s.id ?? '',
        label:
          s.label ||
          s.nomeCompleto ||
          [s.nome, s.cognome].filter(Boolean).join(' ').trim()
      }))
      .filter((s) => s.id !== '' && s.label);
  };

  const normalizeOra = (ora) => {
    if (!ora) return '';
    return String(ora).substring(0, 5);
  };

  useEffect(() => {
    const fetchCategorie = async () => {
      setLoadingCategorie(true);

      try {
        const res = await authFetch(`${API_URL}/api/allenamenti/mie-categorie`);

        if (!res.ok) {
          throw new Error(await parseErrorMessage(res, 'Errore nel caricamento categorie'));
        }

        const data = await res.json();
        const categorieNormalizzate = normalizeCategorie(data);

        setCategorie(categorieNormalizzate);

        // imposta automaticamente la categoria dello staff loggato
        if (categorieNormalizzate.length > 0) {
          setIdCategoria((prev) => {
            if (prev && categorieNormalizzate.some((c) => String(c.id) === String(prev))) {
              return prev;
            }
            return String(categorieNormalizzate[0].id);
          });
        } else {
          setIdCategoria('');
        }
      } catch (err) {
        setCategorie([]);
        setIdCategoria('');
        toast.error(err.message || 'Errore nel caricamento categorie');
      } finally {
        setLoadingCategorie(false);
      }
    };

    fetchCategorie();
  }, [authFetch]);

  useEffect(() => {
    const fetchStaff = async () => {
      setLoadingStaff(true);

      try {
        let res = await authFetch(`${API_URL}/api/admin/lookup/staff`);

        if (!res.ok) {
          res = await authFetch(`${API_URL}/api/admin/staff`);
        }

        if (!res.ok) {
          setStaffList([]);
          return;
        }

        const data = await res.json();
        setStaffList(normalizeStaff(data));
      } catch {
        setStaffList([]);
      } finally {
        setLoadingStaff(false);
      }
    };

    fetchStaff();
  }, [authFetch]);

  const staffById = useMemo(() => {
    const m = new Map();
    staffList.forEach((s) => m.set(String(s.id), s.label));
    return m;
  }, [staffList]);

  const categoriaById = useMemo(() => {
    const m = new Map();
    categorie.forEach((c) => m.set(String(c.id), c.nome));
    return m;
  }, [categorie]);

  const loadAllenamenti = async () => {
    toast.dismiss();
    setAllenamenti([]);

    if (!idCategoria) {
      toast.warning('Seleziona una categoria');
      return;
    }

    if ((from && !to) || (!from && to)) {
      toast.warning('Se imposti il periodo devi inserire sia FROM che TO');
      return;
    }

    if (from && to && from > to) {
      toast.warning('La data FROM non può essere successiva alla data TO');
      return;
    }

    setLoading(true);

    try {
      let url = `${API_URL}/api/allenamenti/categoria/${idCategoria}`;

      if (from && to) {
        url = `${API_URL}/api/allenamenti/categoria/${idCategoria}/periodo?from=${from}&to=${to}`;
      }

      const res = await authFetch(url);

      if (!res.ok) {
        throw new Error(await parseErrorMessage(res, `Errore fetch allenamenti: ${res.status}`));
      }

      const data = await res.json();
      setAllenamenti(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error(err.message || 'Errore nel caricamento allenamenti');
    } finally {
      setLoading(false);
    }
  };

  const openEdit = (allenamento) => {
    setSelected({
      ...allenamento,
      idCategoria: allenamento.idCategoria ?? '',
      dataAllenamento: allenamento.dataAllenamento ?? '',
      oraInizio: normalizeOra(allenamento.oraInizio),
      oraFine: normalizeOra(allenamento.oraFine),
      campo: allenamento.campo ?? '',
      tipoAllenamento: allenamento.tipoAllenamento ?? '',
      idStaffAllenatoreResponsabile: allenamento.idStaffAllenatoreResponsabile ?? '',
      faseAttivazione: allenamento.faseAttivazione ?? '',
      durataAttivazione: allenamento.durataAttivazione ?? '',
      faseCentrale: allenamento.faseCentrale ?? '',
      durataCentrale: allenamento.durataCentrale ?? '',
      faseFinale: allenamento.faseFinale ?? '',
      durataFinale: allenamento.durataFinale ?? ''
    });
    setModalOpen(true);
  };

  const closeEdit = () => {
    setModalOpen(false);
    setSelected(null);
  };

  const validateDurata = (value, label) => {
    if (value === '' || value === null || value === undefined) return;

    const n = Number(value);

    if (Number.isNaN(n) || n <= 0) {
      throw new Error(`${label} deve essere maggiore di 0.`);
    }
  };

  const validatePayload = (row) => {
    if (!row.idCategoria) {
      throw new Error('Categoria obbligatoria.');
    }

    if (!row.dataAllenamento) {
      throw new Error('Data allenamento obbligatoria.');
    }

    if (!row.oraInizio) {
      throw new Error('Ora inizio obbligatoria.');
    }

    if (!row.oraFine) {
      throw new Error('Ora fine obbligatoria.');
    }

    if (row.oraFine <= row.oraInizio) {
      throw new Error("L'ora fine deve essere successiva all'ora inizio.");
    }

    validateDurata(row.durataAttivazione, 'Durata attivazione');
    validateDurata(row.durataCentrale, 'Durata fase centrale');
    validateDurata(row.durataFinale, 'Durata fase finale');
  };

  const buildPayload = (row) => ({
    idCategoria: Number(row.idCategoria),
    dataAllenamento: row.dataAllenamento,
    oraInizio: row.oraInizio,
    oraFine: row.oraFine,
    campo: row.campo?.trim() || null,
    tipoAllenamento: row.tipoAllenamento || null,
    faseAttivazione: row.faseAttivazione?.trim() || null,
    durataAttivazione: row.durataAttivazione ? Number(row.durataAttivazione) : null,
    faseCentrale: row.faseCentrale?.trim() || null,
    durataCentrale: row.durataCentrale ? Number(row.durataCentrale) : null,
    faseFinale: row.faseFinale?.trim() || null,
    durataFinale: row.durataFinale ? Number(row.durataFinale) : null,
    idStaffAllenatoreResponsabile: row.idStaffAllenatoreResponsabile
      ? Number(row.idStaffAllenatoreResponsabile)
      : null
  });

  const saveEdit = async (formData) => {
    toast.dismiss();

    if (!selected?.id) {
      toast.error('Allenamento non selezionato');
      return;
    }

    try {
      validatePayload(formData);
      const payload = buildPayload(formData);

      const res = await authFetch(`${API_URL}/api/allenamenti/${selected.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (!res.ok) {
        throw new Error(await parseErrorMessage(res, 'Errore durante la modifica'));
      }

      toast.success('Allenamento aggiornato');
      closeEdit();
      await loadAllenamenti();
    } catch (err) {
      toast.error(err.message || 'Errore durante il salvataggio');
    }
  };

  const disattiva = async (id) => {
    toast.dismiss();

    const ok = window.confirm('Vuoi disattivare questo allenamento?');
    if (!ok) return;

    try {
      const res = await authFetch(`${API_URL}/api/allenamenti/${id}/disattiva`, {
        method: 'PATCH'
      });

      if (!res.ok) {
        throw new Error(await parseErrorMessage(res, 'Errore durante la disattivazione'));
      }

      toast.success('Allenamento disattivato');
      await loadAllenamenti();
    } catch (err) {
      toast.error(err.message || 'Errore durante la disattivazione');
    }
  };

  const formatData = (data) => {
    if (!data) return '-';

    const value = String(data);

    if (value.includes('-')) {
      const parts = value.split('-');
      if (parts.length === 3) {
        const [yyyy, mm, dd] = parts;
        return `${dd}/${mm}/${yyyy}`;
      }
    }

    return value;
  };

  const formatOra = (ora) => {
    if (!ora) return '-';
    return String(ora).substring(0, 5);
  };

  return (
    <div className="aggiorna-allenamenti-container">
      <h2>Gestione Allenamenti</h2>

      <div className="filtri-allenamenti">
        <div className="filtro">
          <label>Categoria</label>
          <select
            value={idCategoria}
            onChange={(e) => setIdCategoria(e.target.value)}
            disabled={loadingCategorie}
          >
            <option value="">{loadingCategorie ? 'Caricamento...' : '-'}</option>
            {categorie.map((c) => (
              <option key={c.id} value={c.id}>
                {c.nome}
              </option>
            ))}
          </select>
        </div>

        <div className="filtro">
          <label>From</label>
          <input
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
          />
        </div>

        <div className="filtro">
          <label>To</label>
          <input
            type="date"
            value={to}
            onChange={(e) => setTo(e.target.value)}
          />
        </div>

        <button type="button" onClick={loadAllenamenti} disabled={loading}>
          {loading ? 'Caricamento...' : 'Cerca'}
        </button>
      </div>

      <div className="allenamenti-list">
        {allenamenti.length === 0 && !loading ? (
          <div className="allenamenti-empty">
            Nessun allenamento trovato
          </div>
        ) : (
          allenamenti.map((a) => (
            <article className="allenamento-card" key={a.id}>
              <div className="allenamento-card-header">
                <div className="allenamento-card-title">
                  <h3>{formatData(a.dataAllenamento)}</h3>
                  <p>
                    {formatOra(a.oraInizio)} - {formatOra(a.oraFine)}
                  </p>
                </div>

                <div className="allenamento-card-badges">
                  <span className="allenamento-badge">
                    {a.nomeCategoria || categoriaById.get(String(a.idCategoria)) || '-'}
                  </span>

                  <span className="allenamento-badge secondario">
                    {a.tipoAllenamento || 'Tipo non specificato'}
                  </span>
                </div>
              </div>

              <div className="allenamento-card-info">
                <div className="allenamento-info-item">
                  <span className="allenamento-info-label">Campo</span>
                  <span className="allenamento-info-value">{a.campo || '-'}</span>
                </div>

                <div className="allenamento-info-item">
                  <span className="allenamento-info-label">Allenatore</span>
                  <span className="allenamento-info-value">
                    {a.nomeAllenatoreResponsabile ||
                      (a.idStaffAllenatoreResponsabile
                        ? staffById.get(String(a.idStaffAllenatoreResponsabile))
                        : '-') ||
                      '-'}
                  </span>
                </div>
              </div>

              <div className="allenamento-fasi">
                <div className="fase-card">
                  <div className="fase-card-header">
                    <span>1. Attivazione</span>
                    <strong>
                      {a.durataAttivazione ? `${a.durataAttivazione} min` : '-'}
                    </strong>
                  </div>
                  <p>{a.faseAttivazione || '-'}</p>
                </div>

                <div className="fase-card">
                  <div className="fase-card-header">
                    <span>2. Fase centrale</span>
                    <strong>
                      {a.durataCentrale ? `${a.durataCentrale} min` : '-'}
                    </strong>
                  </div>
                  <p>{a.faseCentrale || '-'}</p>
                </div>

                <div className="fase-card">
                  <div className="fase-card-header">
                    <span>3. Fase finale</span>
                    <strong>
                      {a.durataFinale ? `${a.durataFinale} min` : '-'}
                    </strong>
                  </div>
                  <p>{a.faseFinale || '-'}</p>
                </div>
              </div>

              <div className="allenamento-card-actions">
                <button
                  type="button"
                  className="allenamento-btn-edit"
                  onClick={() => openEdit(a)}
                >
                  Modifica
                </button>
                <button
                  type="button"
                  className="allenamento-btn-disable"
                  onClick={() => disattiva(a.id)}
                >
                  Disattiva
                </button>
              </div>
            </article>
          ))
        )}
      </div>

      <div className="allenamenti-back">
        <button
          className="allenamenti-custom-button"
          type="button"
          onClick={() => navigate('/gestionale')}
        >
          Torna al Gestionale
        </button>
      </div>

      <ModaleModificaAllenamento
        open={modalOpen}
        onClose={closeEdit}
        allenamento={selected}
        categorie={categorie}
        staffList={staffList}
        tipiAllenamento={TIPI_ALLENAMENTO}
        onSave={saveEdit}
        loadingStaff={loadingStaff}
        loadingCategorie={loadingCategorie}
      />
    </div>
  );
}